import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthService } from '../services/auth';
import EmailInput from '../components/EmailInput';
import { SwatchBook } from 'lucide-react'; // Import SwatchBook icon

const authService = new AuthService();

const EmailVerification = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.email) {
      setEmail(location.state.email);
    }
    
    // Delay the form fade-in animation
    const timer = setTimeout(() => {
      setFormVisible(true);
    }, 250); // 1/4 second delay
    
    return () => clearTimeout(timer);
  }, [location.state]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      const userId = location.state?.userId;
      if (!userId) {
        throw new Error('User information lost – please sign-up again.');
      }
  
      await authService.confirmAndCreateUser(email, code, userId);
      setMessage('Email verified. You can now log in.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Add an effect to check for missing state
  useEffect(() => {
    // If page is refreshed and state is lost, show a recovery message
    if (!location.state?.email && !location.state?.userId) {
      setError('Verification information lost. Please try signing up again.');
    }
  }, [location.state]);

  const handleResend = async () => {
    setResending(true);
    setError(null);
    try {
      await authService.resendConfirmationCode(email);
      setMessage('Verification code resent. Please check your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-md mx-auto px-4">
        {/* Logo and brand name with subtle animation - stacked vertically */}
        <Link 
          to="/" 
          className="flex flex-col items-center justify-center mb-6 transform transition-transform duration-300 hover:scale-105"
        >
          <SwatchBook className="w-12 h-12 text-orange-500 mb-2" />
          <span className="text-2xl font-bold text-black">dessino.ai</span>
        </Link>
        
        <div 
          className={`bg-white p-6 rounded-lg shadow-sm transition-all duration-500 ease-in-out ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <h1 className="text-2xl font-bold mb-5">Verify Your Email</h1>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 animate-pulse">
              {error}
            </div>
          )}
          
          {message && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              {message}
            </div>
          )}
          
          <form onSubmit={handleConfirm}>
            {email ? (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-gray-700">
                  Verification code sent to: <strong>{email}</strong>
                </p>
              </div>
            ) : (
              <div className="mb-4">
                <EmailInput value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                id="verificationCode"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter the 6-digit code"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-all duration-300 flex items-center justify-center transform hover:scale-[1.01] active:scale-[0.99] mb-3"
            >
              {loading ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></span>
              ) : null}
              {loading ? 'Verifying...' : 'Confirm Email'}
            </button>
            
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="w-full bg-gray-200 text-gray-800 py-3 rounded-md hover:bg-gray-300 transition-all duration-300 flex items-center justify-center"
            >
              {resending ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-gray-500 mr-2"></span>
              ) : null}
              {resending ? 'Sending...' : 'Resend Code'}
            </button>
          </form>
          
          <p className="text-center text-sm text-gray-600 mt-6">
            Already verified?{' '}
            <Link to="/login" className="text-blue-500 hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;